/**
 * mb-ingredients.js — BASIS. Mango Butter
 * - Desktop: mouse-tracking 3D tilt + gloss sweep
 * - Mobile: touch-tracking tilt (swipe = scroll, press/drag = tilt)
 * - Mobile slider: arrow buttons + dot pagination via offsetLeft
 */

(function () {
  'use strict';

  /* ── 3D Tilt (desktop mouse + mobile touch) ──────────────────── */
  function initTilt() {
    const perspectives = document.querySelectorAll('[data-ing-perspective]');
    if (!perspectives.length) return;

    perspectives.forEach(function (perspective) {
      const card  = perspective.querySelector('[data-ing-card]');
      const gloss = perspective.querySelector('[data-ing-gloss]');
      if (!card) return;

      let raf      = null;
      let targetX  = 0, targetY  = 0;
      let currentX = 0, currentY = 0;

      function lerp(a, b, t) { return a + (b - a) * t; }

      function animate() {
        currentX = lerp(currentX, targetX, 0.12);
        currentY = lerp(currentY, targetY, 0.12);
        card.style.transform =
          `rotateX(${currentY}deg) rotateY(${currentX}deg)`;
        if (Math.abs(currentX - targetX) + Math.abs(currentY - targetY) > 0.01) {
          raf = requestAnimationFrame(animate);
        } else {
          raf = null;
        }
      }

      function applyTilt(clientX, clientY) {
        const rect = perspective.getBoundingClientRect();
        const x    = (clientX - rect.left  - rect.width  / 2) / (rect.width  / 2);
        const y    = (clientY - rect.top   - rect.height / 2) / (rect.height / 2);
        targetX    =  x * 13;
        targetY    = -y * 13;
        if (!raf) raf = requestAnimationFrame(animate);
      }

      function resetTilt() {
        targetX = 0;
        targetY = 0;
        if (!raf) raf = requestAnimationFrame(animate);
      }

      function updateGloss(clientX, clientY) {
        if (!gloss) return;
        const rect = perspective.getBoundingClientRect();
        const px   = ((clientX - rect.left) / rect.width)  * 100;
        const py   = ((clientY - rect.top)  / rect.height) * 100;
        gloss.style.background =
          `radial-gradient(circle at ${px}% ${py}%, rgba(255,255,255,0.22) 0%, transparent 58%)`;
      }

      /* ── Desktop mouse ───────────────────────────────────────── */
      perspective.addEventListener('mousemove', function (e) {
        applyTilt(e.clientX, e.clientY);
        updateGloss(e.clientX, e.clientY);
      });

      perspective.addEventListener('mouseleave', function () {
        resetTilt();
        if (gloss) gloss.style.background =
          'radial-gradient(circle at 50% 50%, rgba(255,255,255,0.12) 0%, transparent 65%)';
      });

      /* ── Mobile touch ────────────────────────────────────────── */
      let touchStartX  = 0;
      let isSwiping    = false;
      const SWIPE_THRESHOLD = 14; /* px before we call it a horizontal swipe */

      perspective.addEventListener('touchstart', function (e) {
        if (e.touches.length !== 1) return;
        touchStartX = e.touches[0].clientX;
        isSwiping   = false;
      }, { passive: true });

      perspective.addEventListener('touchmove', function (e) {
        if (e.touches.length !== 1) return;
        const touch = e.touches[0];
        const dx = Math.abs(touch.clientX - touchStartX);

        /* Horizontal swipe → let the slider scroll, skip tilt */
        if (dx > SWIPE_THRESHOLD) { isSwiping = true; }
        if (isSwiping) { resetTilt(); return; }

        /* Mostly vertical / stationary → apply tilt */
        /* Smaller max angle on mobile (8deg vs 13deg desktop) */
        const rect = perspective.getBoundingClientRect();
        const x    = (touch.clientX - rect.left  - rect.width  / 2) / (rect.width  / 2);
        const y    = (touch.clientY - rect.top   - rect.height / 2) / (rect.height / 2);
        targetX    =  x * 8;
        targetY    = -y * 8;
        if (!raf) raf = requestAnimationFrame(animate);
      }, { passive: true });

      perspective.addEventListener('touchend', function () {
        isSwiping = false;
        resetTilt();
      }, { passive: true });
    });
  }

  /* ── Mobile Slider ───────────────────────────────────────────── */
  function initSlider() {
    const grid    = document.querySelector('[data-ing-grid]');
    const prevBtn = document.querySelector('[data-ing-prev]');
    const nextBtn = document.querySelector('[data-ing-next]');
    const dots    = document.querySelectorAll('[data-ing-dot]');

    if (!grid || !prevBtn || !nextBtn) return;

    const mq = window.matchMedia('(max-width: 640px)');
    if (!mq.matches) return;

    const items   = Array.from(grid.querySelectorAll('[data-ing-perspective]'));
    const perPage = 2;
    const pages   = Math.ceil(items.length / perPage);
    let   page    = 0;

    function updateDots() {
      dots.forEach(function (d, i) {
        d.classList.toggle('is-active', i === page);
      });
    }

    function updateArrows() {
      prevBtn.style.opacity = page === 0         ? '0.35' : '1';
      nextBtn.style.opacity = page === pages - 1 ? '0.35' : '1';
      prevBtn.disabled      = page === 0;
      nextBtn.disabled      = page === pages - 1;
    }

    function goTo(p) {
      page = Math.max(0, Math.min(pages - 1, p));

      /* Use the target item's actual DOM offset — reliable regardless
         of margin/padding/gap values in CSS                        */
      const targetItem = items[page * perPage];
      if (targetItem) {
        /*
          Subtract 20px (grid's padding-left) so the card appears with
          20px of breathing room on the left, matching the right side.
          Math.max(0) prevents negative scroll on page 0.
        */
        const left = Math.max(0, targetItem.offsetLeft - 20);
        grid.scrollTo({ left: left, behavior: 'smooth' });
      }

      updateDots();
      updateArrows();
    }

    prevBtn.addEventListener('click', function () { goTo(page - 1); });
    nextBtn.addEventListener('click', function () { goTo(page + 1); });

    dots.forEach(function (d, i) {
      d.addEventListener('click', function () { goTo(i); });
    });

    /* Sync page indicator when user swipes natively */
    let syncTimer;
    grid.addEventListener('scroll', function () {
      clearTimeout(syncTimer);
      syncTimer = setTimeout(function () {
        /* Find which page by comparing scroll position to each item's offsetLeft */
        let closest = 0;
        let minDiff = Infinity;
        for (let i = 0; i < pages; i++) {
          const item = items[i * perPage];
          if (!item) continue;
          const snapLeft = Math.max(0, item.offsetLeft - 20);
          const diff = Math.abs(grid.scrollLeft - snapLeft);
          if (diff < minDiff) { minDiff = diff; closest = i; }
        }
        page = closest;
        updateDots();
        updateArrows();
      }, 80);
    }, { passive: true });

    /* Init */
    updateDots();
    updateArrows();
  }

  /* ── Boot ────────────────────────────────────────────────────── */
  function init() {
    /* Double rAF ensures DOM is fully laid out before we read offsets */
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        initTilt();
        initSlider();
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
